// inputRules — the PURE markdown input-rule engine, generalising the sketch's
// `detectPrefix`/`applyPrefix` (sketches/editor/DocSketch.tsx) into a small
// declarative table plus two entry points, so a new prefix is a new `Rule` row
// rather than a new `if` branch. Alongside it: the `/` slash-trigger's own pure
// decision function (nextSlashTrigger) — the second "typed prefix at block
// start" family this editor recognises, sharing this file because both are the
// same shape of concern (recognise a prefix, decide what firing it means).
//
// @bilda/client imports zero @bilda/core (see edit.ts's header) — this module goes
// further and imports zero DOM too: no textarea, no keyboard event, no caret.
// Every function is text-in/decision-out. The IMPURE half (which textarea holds
// the caret, when a keystroke fires this module's functions, how the resulting
// `RuleEffect`/`SlashTriggerState` gets applied) is a shell concern outside
// @bilda/client's component tree — see this file's HANDOFF note in the SlashMenu
// mount inside MaterialDocument.tsx.
//
// Discipline (dev-ts, CLAUDE.md): every rule is its own named row in a table,
// not a branch in an if/else chain (MARKDOWN_RULES below); every effect is a
// named discriminated union (RuleEffect); the only EditCommand this module ever
// emits is `RetypeElement` from edit.ts — it never invents a UI-only mutation.

import { headingToProse, proseToHeading, type RetypeElement } from "./edit.js";
import type { HeadingElement, ProseElement } from "./types.js";

// === Markdown rules ============================================================

// One declarative row: a name (test IDs / no user-facing meaning), a prefix
// DETECTOR (pure: text in, the remainder after the prefix out, or `undefined`
// when the text does not start with this prefix), and what firing it PRODUCES
// once detected. Adding a rule is adding a row to MARKDOWN_RULES, never a new
// branch in matchRule/applyRule — both stay generic over the table.
export type Rule = {
    readonly name: string;
    readonly detect: (text: string) => { readonly rest: string } | undefined;
    readonly toEffect: (context: RuleContext, rest: string) => RuleEffect;
};

// What a rule needs to build its effect: the prose block being typed into (its
// id carries through to the retype; its full text is NOT read here — the
// caller passes the already-detected `rest` instead, so a rule never re-derives
// the split its own `detect` already computed).
export type RuleContext = {
    readonly prose: ProseElement;
};

// A rule matched against some text: WHICH rule, and the text remaining once its
// prefix (marker + trailing whitespace) is stripped — what the author keeps
// typing once the rule fires.
export type RuleMatch = {
    readonly rule: Rule;
    readonly rest: string;
};

// What firing a matched rule means. `retype` carries the ready-to-dispatch
// `RetypeElement` UI command (edit.ts's fold applies it — this module never
// touches the element tree itself). `deferred` names a rule that MATCHED but
// has no Element kind to retype into yet — bullet/numbered lists, since
// Editor/types.ts's `Element` sum has no list kind (see the MVP-shortcut note
// on MARKDOWN_RULES below). Reported explicitly rather than silently dropped,
// so a caller can choose to no-op or surface "not yet supported".
export type RetypeEffect = {
    readonly type: "retype";
    readonly command: RetypeElement;
};
export type DeferredEffect = {
    readonly type: "deferred";
    readonly kind: "bullet-list" | "numbered-list";
};
export type RuleEffect = RetypeEffect | DeferredEffect;

// Find the first rule in `rules` whose prefix matches the START of `text`.
// Order matters only in principle (MARKDOWN_RULES's patterns do not overlap —
// `^#\s` cannot also match `^##\s`'s text — so today's table is order-
// independent, but a future rule with an overlapping prefix would need to sit
// before the rule it should shadow). Pure; `undefined` ⇒ no rule fires.
export const matchRule = (
    rules: readonly Rule[],
    text: string,
): RuleMatch | undefined => {
    for (const rule of rules) {
        const detected = rule.detect(text);
        if (detected !== undefined) return { rule, rest: detected.rest };
    }
    return undefined;
};

// Build the effect of an already-matched rule. Split from `matchRule` so a
// caller can inspect WHICH rule matched (e.g. to decide whether to call this at
// all) before committing to building the effect.
export const applyRule = (context: RuleContext, match: RuleMatch): RuleEffect =>
    match.rule.toEffect(context, match.rest);

// A "marker + one space" heading rule — `#`/`##`/`###` fire on the Space right
// after the marker (Notion/Craft semantics): the text a caller passes in has
// already had the space typed into it, so `detect` simply requires it present.
const headingRule = (marker: string, level: 1 | 2 | 3): Rule => {
    const pattern = new RegExp(`^${marker}\\s`);
    return {
        name: `heading-${level}`,
        detect: (text) => {
            const match = pattern.exec(text);
            return match === null
                ? undefined
                : { rest: text.slice(match[0].length) };
        },
        toEffect: (context, rest) => ({
            type: "retype",
            command: {
                type: "retype-element",
                id: context.prose.id,
                to: proseToHeading({ ...context.prose, markdown: rest }, level),
            },
        }),
    };
};

// `-`/`*` and `1.` fire the same way but have no Element kind to land in yet
// (SHORTCUT, stated per the lifecycle-design principle: Editor/types.ts's
// `Element` sum has no list kind — adding one is a later slice, not this one).
// They still MATCH (so a caller can strip the marker / show feedback) and
// still produce a typed effect (`deferred`), never silently doing nothing.
const bulletListRule: Rule = {
    name: "bullet-list",
    detect: (text) => {
        const match = /^[-*]\s/.exec(text);
        return match === null
            ? undefined
            : { rest: text.slice(match[0].length) };
    },
    toEffect: (_context, _rest) => ({ type: "deferred", kind: "bullet-list" }),
};

const numberedListRule: Rule = {
    name: "numbered-list",
    detect: (text) => {
        const match = /^\d+\.\s/.exec(text);
        return match === null
            ? undefined
            : { rest: text.slice(match[0].length) };
    },
    toEffect: (_context, _rest) => ({
        type: "deferred",
        kind: "numbered-list",
    }),
};

// The preset table: `#` / `##` / `###` / `-` / `1.` — the MVP slice's markdown
// rules (document-first-editor.md §3). Order-independent today (see matchRule).
export const MARKDOWN_RULES: readonly Rule[] = [
    headingRule("#", 1),
    headingRule("##", 2),
    headingRule("###", 3),
    bulletListRule,
    numberedListRule,
];

// Revert a heading back to prose — the Backspace-at-empty-start undo of a fired
// markdown rule (DocSketch's convention: one Backspace undoes the transform
// before it starts deleting the previous block). The caller decides WHEN this
// applies (Backspace + caret at block start + block text empty is a DOM/caret
// fact this module never inspects); this is the pure "what happens" half.
export const revertHeadingToProse = (
    heading: HeadingElement,
): RetypeElement => ({
    type: "retype-element",
    id: heading.id,
    to: headingToProse(heading),
});

// === Slash-trigger (pure, ephemeral) ==========================================
// Whether the `/` insert menu should be open for a block's CURRENT text — the
// same rule DocSketch's `SlashState`/`dismissedId` pair encodes (isSlash +
// escape hatch), made a named pure state + reducer. This governs the SlashMenu
// mount inside MaterialDocument.tsx (see its HANDOFF note): the shell that owns
// the real textarea calls `nextSlashTrigger` on every keystroke and `dismiss`
// on Escape; @bilda/client never reads a DOM event itself.
export type SlashTriggerState = "closed" | "open" | "dismissed";

// A leading `/` with no whitespace after it yet — still being typed as a query,
// not yet abandoned with a space.
const isSlashPrefix = (text: string): boolean =>
    text.startsWith("/") && !/\s/.test(text);

// The next state, given the PRIOR state and the block's text after a keystroke.
// `"//"` is the explicit "I meant a literal slash" back-out (Notion/Slack): it
// collapses to `dismissed` regardless of the prior state. Once `dismissed`, the
// trigger stays dismissed for as long as the text still starts with `/` — it
// only re-arms (`closed`) once the leading `/` is gone, so a single Escape or
// `//` cannot be immediately re-triggered by the next keystroke of the same
// abandoned slash. A `dismissed` state's own text mutation (collapsing "//" to
// a literal "/") is the CALLER's job — this function only decides the mode.
export const nextSlashTrigger = (
    prior: SlashTriggerState,
    text: string,
): SlashTriggerState => {
    if (text === "//") return "dismissed";
    if (!text.startsWith("/")) return "closed";
    if (prior === "dismissed") return "dismissed";
    return isSlashPrefix(text) ? "open" : "closed";
};

// Back out of an open menu without touching the typed text — the Escape key.
// Nullary: unlike `nextSlashTrigger` it needs no text, because Escape's meaning
// does not depend on what is currently typed.
export const dismissSlashTrigger = (): SlashTriggerState => "dismissed";
